import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Dialog, Select, TextField, Textarea } from '../ui';

/** Adding or editing an AI configuration (admin, deprecated page). The prompt templates stay in English: they are sent to the model. */
function AIConfigurationModal({ configuration, onSave, onClose }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    api_type: 'claude',
    api_url: '',
    api_key: '',
    model_name: '',
    prompt_template: '',
    max_tokens: 4000,
    temperature: 0.7,
    is_active: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (configuration) {
      setFormData({
        name: configuration.name || '',
        api_type: configuration.api_type || 'claude',
        api_url: configuration.api_url || '',
        api_key: '', // Always start empty for security
        model_name: configuration.model_name || '',
        prompt_template: configuration.prompt_template || '',
        max_tokens: parseInt(configuration.max_tokens) || 4000,
        temperature: parseFloat(configuration.temperature) || 0.7,
        is_active: Boolean(configuration.is_active)
      });
    } else {
      // Set defaults for new configuration
      setFormData({
        name: '',
        api_type: 'claude',
        api_url: 'https://api.anthropic.com/v1/messages',
        api_key: '',
        model_name: 'claude-sonnet-5',
        prompt_template: getDefaultPromptTemplate('claude'),
        max_tokens: 4000,
        temperature: 0.7,
        is_active: false
      });
    }
  }, [configuration]);

  const getDefaultPromptTemplate = (apiType) => {
    if (apiType === 'claude') {
      return `Please analyze this image and extract all items/products listed. This is for inventory management in a "{inventory_name}" inventory.

For each item found, please provide the following fields:
{field_descriptions}
{location_info}

Instructions:
1. Look for any lists, tables, invoices, catalogs, or handwritten notes containing items
2. Extract as much information as possible for each field
3. For fields with specific options, only use the provided options
4. If information is not clearly visible, leave the field empty rather than guessing
5. Be thorough - capture all items you can identify
6. For quantities, if you see "x2" or "2x", create separate items for each unit

Please respond with a JSON array where each object represents one item with field_values containing the extracted data.

Example format:
[
  {
    "field_values": {
      "Product Name": "Example Item",
      "Price": "19.99",
      "Category": "Electronics"
    }
  }
]

Respond only with the JSON array, no additional text.`;
    } else if (apiType === 'openai') {
      return `Analyze this image and extract all items/products for inventory management in "{inventory_name}".

Extract data for these fields:
{field_descriptions}
{location_info}

Return only a JSON array with extracted items in this format:
[{"field_values": {"field_name": "value"}}]`;
    } else {
      return `Extract items from this image for "{inventory_name}" inventory.

Fields needed:
{field_descriptions}
{location_info}

Return JSON array: [{"field_values": {"field_name": "value"}}]`;
    }
  };

  const getDefaultApiUrl = (apiType) => {
    switch (apiType) {
      case 'claude':
        return 'https://api.anthropic.com/v1/messages';
      case 'openai':
        return 'https://api.openai.com/v1/chat/completions';
      default:
        return '';
    }
  };

  const getDefaultModelName = (apiType) => {
    switch (apiType) {
      case 'claude':
        return 'claude-sonnet-5';
      case 'openai':
        return 'gpt-4o';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleApiTypeChange = (e) => {
    const newApiType = e.target.value;
    setFormData(prev => ({
      ...prev,
      api_type: newApiType,
      api_url: getDefaultApiUrl(newApiType),
      model_name: getDefaultModelName(newApiType),
      prompt_template: getDefaultPromptTemplate(newApiType)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('aiConfig.form.nameRequired');
    }

    if (!formData.api_url.trim()) {
      newErrors.api_url = t('aiConfig.form.urlRequired');
    }

    // API key is required for new configurations, but optional for edits (empty means keep current)
    if (!formData.api_key.trim() && formData.api_type !== 'custom' && !configuration) {
      newErrors.api_key = t('aiConfig.form.keyRequired');
    }

    if (formData.max_tokens < 100 || formData.max_tokens > 8000) {
      newErrors.max_tokens = t('aiConfig.form.maxTokensRange');
    }

    if (formData.temperature < 0 || formData.temperature > 2) {
      newErrors.temperature = t('aiConfig.form.temperatureRange');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={configuration ? t('aiConfig.form.editTitle') : t('aiConfig.form.addTitle')}
      size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="ai-configuration">{configuration ? t('common.save') : t('aiConfig.add')}</Button>
        </>
      )}
    >
      <form id="ai-configuration" className="dialog-form" onSubmit={handleSubmit} noValidate>
        <TextField id="name" name="name" label={t('aiConfig.form.name')} value={formData.name} onChange={handleInputChange}
                   placeholder={t('aiConfig.form.namePlaceholder')} error={errors.name} required />
        <Select id="api_type" name="api_type" label={t('aiConfig.col.type')} value={formData.api_type} onChange={handleApiTypeChange}>
          <option value="claude">{t('aiConfig.types.claude')}</option>
          <option value="openai">{t('aiConfig.types.openai')}</option>
          <option value="custom">{t('aiConfig.types.custom')}</option>
        </Select>
        <TextField id="api_url" name="api_url" type="url" label={t('aiConfig.form.url')} value={formData.api_url} onChange={handleInputChange}
                   placeholder="https://api.example.com/v1/endpoint" error={errors.api_url} required />
        <TextField id="api_key" name="api_key" type="password" autoComplete="off" label={t('aiConfig.form.key')} value={formData.api_key}
                   onChange={handleInputChange} error={errors.api_key} required={formData.api_type !== 'custom' && !configuration}
                   placeholder={configuration && configuration.has_api_key ? t('aiConfig.form.keyKeep') : t('aiConfig.form.keyPlaceholder')}
                   hint={configuration && configuration.has_api_key ? t('aiConfig.form.keyCurrent', { key: configuration.api_key_masked }) : undefined} />
        <TextField id="model_name" name="model_name" label={t('aiConfig.col.model')} value={formData.model_name} onChange={handleInputChange}
                   placeholder="claude-sonnet-5" />
        <div className="type-editor__pair">
          <TextField id="max_tokens" name="max_tokens" type="number" min="100" max="8000" label={t('aiConfig.form.maxTokens')}
                     value={formData.max_tokens} onChange={handleInputChange} error={errors.max_tokens} />
          <TextField id="temperature" name="temperature" type="number" min="0" max="2" step="0.1" label={t('aiConfig.form.temperature')}
                     value={formData.temperature} onChange={handleInputChange} error={errors.temperature} />
        </div>
        <Textarea id="prompt_template" name="prompt_template" label={t('aiConfig.form.prompt')} rows={8} className="code-textarea"
                  value={formData.prompt_template} onChange={handleInputChange}
                  hint={t('aiConfig.form.promptHelp', { placeholders: '{inventory_name}, {field_descriptions}, {location_info}' })} />
        <Checkbox name="is_active" label={t('aiConfig.form.setActive')} hint={t('aiConfig.form.setActiveHelp')}
                  checked={formData.is_active} onChange={handleInputChange} />
      </form>
    </Dialog>
  );
}

export default AIConfigurationModal;